import React, { useEffect, useRef, useState } from "react";
import { NavLink as RouterLink } from "react-router-dom";

function useInView({ threshold = 0.25, rootMargin = "0px 0px -15% 0px" } = {}) {
  const ref = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(([entry]) => setActive(entry.isIntersecting), {
      threshold,
      rootMargin,
    });

    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold, rootMargin]);

  return { ref, active };
}

const HBSJ_ASSETS = {
  missionSlides: [
    { image: "/mission/mission1.jpg", label: "" },
    { image: "/mission/mission3.jpg", label: "" },
    { image: "/mission/mission4.jpg", label: "" },
    { image: "/mission/mission5.jpg", label: "" },
    { image: "/mission/mission6.jpg", label: "" },
    { image: "/mission/mission7.jpg", label: "" },
  ],
};

function HBSJCongressionalCaucus() {
  const { ref, active } = useInView({ threshold: 0.2, rootMargin: "0px 0px -20% 0px" });
  const slides = HBSJ_ASSETS.missionSlides;
  const [slideIndex, setSlideIndex] = useState(0);
  const activeSlide = slides[slideIndex];

  useEffect(() => {
    const id = setInterval(() => {
      setSlideIndex((index) => (index + 1) % slides.length);
    }, 4000);

    return () => clearInterval(id);
  }, [slides.length]);

  return (
    <>
      <style>{`
        .hbsj-wrap {
          opacity: 0;
          transform: translateY(12px);
          transition: opacity 700ms ease, transform 700ms ease;
        }

        .hbsj-wrap.is-active {
          opacity: 1;
          transform: translateY(0);
        }

        .hbsj-wrap ul,
        .hbsj-wrap ol,
        .hbsj-wrap li,
        .hbsj-copy {
          text-align: left !important;
        }

        .hra-topTitle::before,
        .hra-topTitle::after,
        .head-title::before,
        .head-title::after {
          display: none !important;
          content: none !important;
          border: none !important;
        }

        .hra-topTitle {
          margin-bottom: 0 !important;
          line-height: 1.18;
        }

        .section-divider {
          width: 100%;
          max-width: 760px;
          margin: 22px auto 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 16px;
        }

        .section-divider::before,
        .section-divider::after {
          content: "";
          flex: 1;
          height: 2px;
          border-radius: 999px;
          background: linear-gradient(to right, transparent, #d4af37, transparent);
        }

        .section-divider span {
          width: 9px;
          height: 9px;
          flex: 0 0 auto;
          border-radius: 999px;
          background: #ff9933;
          box-shadow: 0 0 0 5px rgba(255, 153, 51, 0.16);
        }

        .hra-topIntro {
          margin: 0 auto 18px;
          max-width: 720px;
          line-height: 1.6;
        }

        .hbsj-copy {
          font-size: 15px;
          line-height: 1.75;
        }

        .hbsj-navLinkBtn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 46px;
          padding: 13px 24px;
          border-radius: 999px;
          font-size: 13px;
          font-weight: 800;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #0b2849;
          background: #ff9933;
          border: 1px solid rgba(255, 255, 255, 0.64);
          box-shadow: 0 16px 40px rgba(0, 0, 0, 0.24);
          text-decoration: none;
          transition: transform 220ms ease, box-shadow 220ms ease, background 220ms ease, color 220ms ease;
        }

        .hbsj-navLinkBtn:hover {
          transform: translateY(-2px);
          box-shadow: 0 22px 58px rgba(0, 0, 0, 0.32);
          background: #ffffff;
          color: #0b3a96;
        }

        .hbsj-slideshow {
          width: min(100%, 980px);
          margin: 0 auto;
        }

        .hbsj-slideFrame {
          position: relative;
          width: 100%;
          aspect-ratio: 16 / 9;
          min-height: 320px;
          max-height: 560px;
          border-radius: 8px;
          overflow: hidden;
          background: #081525;
          border: 1px solid rgba(255, 255, 255, 0.16);
          box-shadow: 0 24px 70px rgba(0, 0, 0, 0.34);
        }

        .hbsj-slideImg {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          background: #081525;
          animation: hbsjSimpleFade 520ms ease both;
        }

        @keyframes hbsjSimpleFade {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .hbsj-slideMeta {
          display: block;
          margin-top: 12px;
        }

        .hbsj-slideLabel {
          margin: 0;
          color: #ffffff;
          font-size: 14px;
          font-weight: 700;
          line-height: 1.35;
          text-align: center;
        }

        @media (max-width: 768px) {
          .hra-topTitle {
            font-size: 18px !important;
            line-height: 1.28 !important;
            margin-top: -28px !important;
          }

          .hra-topTitle .uppercase {
            display: block;
            max-width: 340px;
            margin: 0 auto;
          }

          .section-divider {
            margin: 14px auto 18px;
          }

          .hra-topIntro,
          .hbsj-copy,
          .section-title-body p {
            font-size: 13px !important;
            line-height: 1.6 !important;
          }

          .hbsj-navLinkBtn {
            min-height: 42px;
            padding: 11px 18px;
            font-size: 11px;
            letter-spacing: 0.07em;
          }

          .hbsj-slideFrame {
            aspect-ratio: 16 / 9;
            min-height: 0;
            max-height: none;
          }

          .hbsj-slideLabel {
            text-align: center;
            font-size: 13px;
          }

        }

        @media (max-width: 420px) {
          .hbsj-slideFrame {
            min-height: 0;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .hbsj-wrap,
          .hbsj-slideImg,
          .hbsj-navLinkBtn {
            transition: none !important;
            animation: none !important;
            transform: none !important;
          }
        }
      `}</style>

      <section>
        <div className="block color-scheme-4">
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-md-10 col-lg-9">
                <div className="section-sub-title center">
                  <article ref={ref} className={`section-title-body white hbsj-wrap ${active ? "is-active" : ""}`}>
                    <h1 className="head-title hra-topTitle">
                      <span className="uppercase">Hindu Buddhist Sikh Jain (HBSJ) American Congressional Caucus</span>
                      <div className="section-divider" aria-hidden="true">
                        <span />
                      </div>
                    </h1>

                    <p className="hra-topIntro">
                      Supporting advocacy for Hindu, Buddhist, Sikh, and Jain communities in Washington DC.
                    </p>

                    <div className="mt-5">
                      <p className="hbsj-copy">
                        The <b>Hindu, Buddhist, Sikh, and Jain (HBSJ) American Congressional Caucus</b> is a bipartisan group of members of the United States Congress formed to advance understanding, inclusion, and legislative attention toward the shared interests and concerns of America's diverse Dharmic faith communities. Officially launched with support from lawmakers committed to representing religious freedom and combating discrimination, the caucus provides an organized platform within the U.S. legislative process to foster dialogue, raise awareness of issues affecting Hindu, Buddhist, Sikh, and Jain populations, and ensure that their perspectives are meaningfully included in policy discussions.
                      </p>
                      <p className="hbsj-copy">
                        By bringing together elected officials across party lines, the HBSJ Caucus works to address key matters such as combating religious discrimination and bigotry, promoting interfaith harmony and mutual respect, and advancing initiatives related to education, community safety, and empowerment. It serves not only as a forum for collaboration among lawmakers but also as a channel through which these communities can engage with and influence the political process at the federal level. The caucus reflects a broader commitment to the principles of religious liberty and pluralism that are foundational to the American democratic tradition.
                      </p>

                      <div className="mt-5 d-flex justify-content-center">
                        <RouterLink to="/a4h-endorsed-candidates#hbsj-caucus-members" className="hbsj-navLinkBtn">
                          HBSJ Caucus Members
                        </RouterLink>
                      </div>

                      <div className="mt-5">
                        <div className="hbsj-slideshow" aria-label="HBSJ mission slideshow">
                          <div className="hbsj-slideFrame" aria-live="polite">
                            <img
                              key={activeSlide.image}
                              src={activeSlide.image}
                              alt={activeSlide.label}
                              loading="lazy"
                              className="hbsj-slideImg"
                            />
                          </div>

                          <div className="hbsj-slideMeta">
                            <p className="hbsj-slideLabel">
                              {activeSlide.label} ({slideIndex + 1}/{slides.length})
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-5">
                      <h3 className="white">Support Our Mission</h3>
                      <p className="white">
                        <strong>Fuel leadership, advocacy, culture and community empowerment. Support our mission today.</strong>
                      </p>
                    </div>
                  </article>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default HBSJCongressionalCaucus;
